/**
 * Option Strategies (Sudarsan + future) — management + live monitoring (Phase 2.5).
 *
 * Watchlists are the SHARED user-scoped /watchlists (managed on the Watchlist
 * page; instances here only SELECT one). Create & edit forms are driven
 * by the backend strategy REGISTRY (param schema + help → tooltips). Each instance
 * is a tabbed card (modelled on the Strategies tab): Monitor (live state + decision
 * log with reasons), Rules (uniform StrategyOverview), Performance (payoff + P&L),
 * Settings (params / mode / schedule).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, Typography, Button, Select, MenuItem, TextField, FormControl, InputLabel,
  Switch, FormControlLabel, Chip, Table, TableHead, TableRow, TableCell, TableBody, Divider,
  Tooltip, IconButton, Tabs, Tab, Accordion, AccordionSummary, AccordionDetails, Collapse,
  CircularProgress, Dialog, DialogTitle, DialogContent,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import toast from 'react-hot-toast';
import { optionStrategyService } from '../services/optionStrategy.service';
import { StrategyOverview } from '../components/strategies/StrategyOverview';
import { OPTION_OVERVIEW_SPECS } from '../utils/optionStrategyOverview';
import OptionInstancePerformance, { computeInstanceStats } from '../components/options/OptionInstancePerformance';
import OptionInstanceBacktest from '../components/options/OptionInstanceBacktest';
import CycleGrid from '../components/options/CycleGrid';
import { cycleStats } from '../components/options/cycleFormat';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { intervalLabel } from '../utils/intervalCatalog';

const UI_KEY = 'optionAT.ui.v1';
const loadUi = (): { tabByInst?: Record<number, number>; expandedByInst?: Record<number, boolean> } => {
  try { return JSON.parse(localStorage.getItem(UI_KEY) || '{}') || {}; } catch { return {}; }
};
const inrShort = (v: number) => `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`;
const istDay = (t: number) => new Date(t).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

/** Per-symbol running P&L (latest snapshot) + today's P&L (latest − last of prev IST day). */
function perSymbolPnl(events: any[]): Record<string, { running: number; day: number }> {
  const asc = [...(events || [])].filter(e => e.pnl != null).sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  const today = istDay(Date.now());
  const out: Record<string, { running: number; day: number }> = {};
  const prevDayClose: Record<string, number> = {}; // last snapshot strictly before today
  for (const e of asc) {
    const v = Number(e.pnl);
    out[e.symbol] = { running: v, day: 0 };
    if (istDay(new Date(e.at).getTime()) !== today) prevDayClose[e.symbol] = v;
  }
  for (const sym of Object.keys(out)) out[sym].day = out[sym].running - (prevDayClose[sym] || 0);
  return out;
}

const TF_OPTS = [1, 3, 5, 15, 30, 60, 240, 1440];
// The old local tfLabel called 10080 "1D" — harmless only because 1W was not in
// TF_OPTS. Use the shared label so it stays right if this list ever grows.
const tfLabel = intervalLabel;

interface ParamField { key: string; label: string; type: 'number' | 'select' | 'boolean' | 'text'; help: string; default: any; options?: Array<{ value: string; label: string }>; step?: number; min?: number; max?: number; placeholder?: string }
interface Descriptor { id: string; label: string; description: string; params: ParamField[] }

/** Stable accent per strategy engine — used on the card top border AND each running
 *  instance's left bar/header so instances read as belonging to their strategy and
 *  it's easy to see where an instance starts even when sandwiched among others. */
const ENGINE_ACCENTS = ['#1e88e5', '#8e24aa', '#00897b', '#f4511e', '#3949ab', '#c2185b'];
const engineAccent = (engine: string, reg: Descriptor[]): string => {
  const i = reg.findIndex(d => d.id === engine);
  return ENGINE_ACCENTS[(i >= 0 ? i : 0) % ENGINE_ACCENTS.length];
};

const getDeep = (obj: any, path: string) => path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
const setDeep = (obj: any, path: string, val: any) => { const ks = path.split('.'); let o = obj; for (let i = 0; i < ks.length - 1; i++) { o[ks[i]] = o[ks[i]] || {}; o = o[ks[i]]; } o[ks[ks.length - 1]] = val; return obj; };

const BAND_META: Record<string, { abbr: string; label: string; bg: string; fg: string }> = {
  strongShort: { abbr: 'SS', label: 'Strong Short', bg: '#7f1d1d', fg: '#fff' },
  probableShort: { abbr: 'PS', label: 'Probable Short', bg: '#ef5350', fg: '#fff' },
  neutral: { abbr: 'N', label: 'Neutral', bg: '#78909c', fg: '#fff' },
  probableBuy: { abbr: 'PL', label: 'Probable Long', bg: '#26a69a', fg: '#fff' },
  strongBuy: { abbr: 'SL', label: 'Strong Long', bg: '#1b5e20', fg: '#fff' },
};
const BandBadge: React.FC<{ band?: string | null; score?: number | null }> = ({ band, score }) => {
  const m = band ? BAND_META[band] : null;
  if (!m) return <Chip size="small" label="—" sx={{ height: 18, fontSize: '0.6rem' }} />;
  return (
    <Tooltip arrow title={`${m.label}${score != null ? ` · score ${Math.round(score)}` : ''}`}>
      <Chip size="small" label={`${m.abbr}${score != null ? ` ${Math.round(score)}` : ''}`} sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: m.bg, color: m.fg }} />
    </Tooltip>
  );
};

const ParamInput: React.FC<{ field: ParamField; value: any; onChange: (v: any) => void }> = ({ field, value, onChange }) => {
  const inner = field.type === 'boolean' ? (
    <FormControlLabel control={<Switch checked={!!value} onChange={e => onChange(e.target.checked)} />} label={field.label} />
  ) : field.type === 'select' ? (
    <FormControl size="small" sx={{ minWidth: 190 }}>
      <InputLabel>{field.label}</InputLabel>
      <Select label={field.label} value={value ?? field.default} onChange={e => onChange(e.target.value)}>
        {(field.options || []).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  ) : field.type === 'text' ? (
    <TextField size="small" label={field.label} value={value ?? field.default}
      onChange={e => onChange(e.target.value)} placeholder={field.placeholder}
      sx={{ width: 190 }} />
  ) : (
    <TextField size="small" type="number" label={field.label} value={value ?? field.default}
      onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      inputProps={{ step: field.step, min: field.min, max: field.max }} sx={{ width: 190 }} />
  );
  // Keep the help tooltip BELOW the dropdown menu (MUI Menu/Popover z-index 1300)
  // so it never covers the options when a select param is open.
  return <Tooltip title={field.help} arrow placement="top" disableInteractive slotProps={{ popper: { sx: { zIndex: 1250 } } }}>{inner as any}</Tooltip>;
};

const ACTION_COLOR: Record<string, string> = { SELL: '#ef5350', SQUAREOFF: '#26a69a', FLIP: '#fb8c00', NONE: '#90a4ae', WAIT: '#90a4ae', BASELINE: '#90a4ae', DATA_FAIL: '#d32f2f' };

const eslDist = (p: any): number | null => {
  if (p.status === 'FLAT' || p.eslLevel == null || !p.lastUnderlyingLtp) return null;
  const ltp = Number(p.lastUnderlyingLtp); const e = Number(p.eslLevel);
  return p.heldOptType === 'CE' ? ((e - ltp) / ltp) * 100 : ((ltp - e) / ltp) * 100;
};

/** Compact stat shown in the (always-visible) accordion header. */
const HeaderStat: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
    <Typography sx={{ fontSize: '0.54rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: color || 'text.primary' }}>{value}</Typography>
  </Box>
);

const OptionStrategiesPage: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const navigate = useNavigate();
  // This page is now auto-engine management only — outlook-building moved to the
  // Options hub (Trade tab). Kept as a const so the (always-true) instances guard stays.
  const archMode = 'instances' as const;
  const [registry, setRegistry] = useState<Descriptor[]>([]);
  const [instances, setInstances] = useState<any[]>([]);
  const [watchlists, setWatchlists] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  // Create form
  const [strategyId, setStrategyId] = useState('sudarsan');
  const [name, setName] = useState('Sudarsan');
  const [watchlistId, setWatchlistId] = useState<number | ''>('');
  const [intervalMinutes, setIntervalMinutes] = useState(15);
  const [tradingMode, setTradingMode] = useState<'PAPER' | 'LIVE'>('PAPER');
  const [autoTrade, setAutoTrade] = useState(true);
  const [routeDashboard, setRouteDashboard] = useState(true);
  const [routeTelegram, setRouteTelegram] = useState(false);
  const [paramVals, setParamVals] = useState<Record<string, any>>({});
  const [showCreate, setShowCreate] = useState(false);

  // Strategy CARD state — per-engine editable DEFAULT settings (saved server-side),
  // which section of a card is open, and which card is expanded. New instances inherit
  // the saved defaults. (Rules / Backtest / Settings live on the card now.)
  const [defaults, setDefaults] = useState<Record<string, any>>({});
  const [cardParams, setCardParams] = useState<Record<string, Record<string, any>>>({});
  const [cardOpenId, setCardOpenId] = useState<string | null>(null);
  const [cardTab, setCardTab] = useState<Record<string, number>>({});
  const [savingDefault, setSavingDefault] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // Watchlist manager

  // Per-instance UI state — persisted so returning to this page restores the
  // open instances + their active sub-tab (issue #2).
  const [tabByInst, setTabByInst] = useState<Record<number, number>>(() => loadUi().tabByInst || {});
  const [expandedByInst, setExpandedByInst] = useState<Record<number, boolean>>(() => loadUi().expandedByInst || {});
  const [logOpenByInst, setLogOpenByInst] = useState<Record<number, boolean>>(() => (loadUi() as any).logOpenByInst || {});
  const [logByInst, setLogByInst] = useState<Record<number, any[]>>({});

  useEffect(() => { try { localStorage.setItem(UI_KEY, JSON.stringify({ tabByInst, expandedByInst, logOpenByInst })); } catch { /* ignore */ } }, [tabByInst, expandedByInst, logOpenByInst]);

  const descriptor = registry.find(d => d.id === strategyId);

  const loadLog = useCallback(async (id: number) => {
    try { const r = await optionStrategyService.log(id); setLogByInst(prev => ({ ...prev, [id]: r.data?.data || [] })); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reg, r, w, def] = await Promise.all([
        registry.length ? Promise.resolve({ data: { data: registry } }) : optionStrategyService.registry(),
        optionStrategyService.list(), optionStrategyService.watchlists(),
        optionStrategyService.getDefaults().catch(() => ({ data: { data: {} } })),
      ]);
      const regData: Descriptor[] = reg.data?.data || [];
      setRegistry(regData);
      const defs: Record<string, any> = def.data?.data || {};
      setDefaults(defs);
      // Seed each card's editable defaults from the saved config (falling back to the
      // registry param defaults). Only seed once so the user's in-progress edits stick.
      setCardParams(prev => {
        if (Object.keys(prev).length) return prev;
        const cp: Record<string, Record<string, any>> = {};
        for (const d of regData) {
          const init: Record<string, any> = {};
          d.params.forEach(p => { const saved = getDeep(defs[d.id], p.key); init[p.key] = saved === undefined ? p.default : saved; });
          cp[d.id] = init;
        }
        return cp;
      });
      const insts = r.data?.data || [];
      setInstances(insts);
      const wls = w.data?.data || [];
      setWatchlists(wls);
      setWatchlistId(prev => (prev === '' && wls.length ? wls[0].id : prev));
      setParamVals(prev => {
        if (Object.keys(prev).length) return prev;
        const d = regData.find(x => x.id === strategyId);
        const init: Record<string, any> = {}; d?.params.forEach(p => { init[p.key] = p.default; }); return init;
      });
      // Load logs for ALL instances so the (always-visible) accordion headers can
      // show overall + last-trade P&L without the user expanding them (issue #1).
      for (const inst of insts) loadLog(inst.id);
    } catch { /* non-critical */ }
    setLoading(false);
  }, [registry, strategyId, loadLog]);

  useEffect(() => { load(); const id = setInterval(load, 15_000); return () => clearInterval(id); }, [load]);

  const buildConfig = (vals: Record<string, any>, d?: Descriptor) => {
    const cfg: any = {}; d?.params.forEach(p => { if (vals[p.key] !== undefined && vals[p.key] !== '') setDeep(cfg, p.key, vals[p.key]); }); return cfg;
  };

  const create = async () => {
    if (watchlistId === '') { toast.error('Pick a watchlist'); return; }
    setBusy(true);
    try {
      // Instance inherits the card's saved DEFAULT settings for the chosen engine.
      const d = registry.find(x => x.id === strategyId);
      const cfg = buildConfig(cardParams[strategyId] || paramVals, d);
      await optionStrategyService.create({ name, engine: strategyId, watchlistId, intervalMinutes, tradingMode, autoTrade, routeDashboard, routeTelegram, mode: cfg.expiryMode, config: cfg });
      toast.success(`Created "${name}" (${tradingMode})`); setShowCreate(false); load();
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Create failed'); }
    setBusy(false);
  };

  /** Save a strategy card's current params as the per-user DEFAULT for that engine. */
  const saveCardDefault = async (engine: string) => {
    const d = registry.find(x => x.id === engine);
    const cfg = buildConfig(cardParams[engine] || {}, d);
    setSavingDefault(true);
    try {
      await optionStrategyService.saveDefault(engine, cfg);
      setDefaults(prev => ({ ...prev, [engine]: cfg }));
      toast.success(`Saved default settings for ${d?.label || engine} — new instances will use these.`);
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Save failed'); }
    finally { setSavingDefault(false); }
  };

  /** Delete an instance — squares off any open positions first, then removes it. */
  const deleteInstance = async (inst: any) => {
    if (!window.confirm(`Delete "${inst.name}"? Any open positions are squared off first (LIVE = real reduce-only exits), then the instance is removed. This cannot be undone.`)) return;
    setDeletingId(inst.id);
    try {
      const r = await optionStrategyService.remove(inst.id);
      const closed = r.data?.data?.closed ?? 0;
      toast.success(`Deleted "${inst.name}"${closed ? ` — squared off ${closed} position(s)` : ''}`);
      load();
    } catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Delete failed'); }
    finally { setDeletingId(null); }
  };

  // (Watchlist create/edit/delete removed — managed on the Watchlist page.)

  // Instance actions
  const toggleEnabled = async (inst: any, enabled: boolean) => { try { await optionStrategyService.update(inst.id, { enabled }); toast.success(enabled ? 'Resumed' : 'Paused'); load(); } catch { toast.error('Update failed'); } };
  // Scan routing trio — Auto-Trade / Dashboard / Telegram (honored by the scanner engines).
  const toggleScanFlag = async (inst: any, key: 'autoTrade' | 'routeDashboard' | 'routeTelegram', val: boolean) => {
    try { await optionStrategyService.update(inst.id, { [key]: val }); toast.success(`${key === 'autoTrade' ? 'Auto-Trade' : key === 'routeTelegram' ? 'Telegram' : 'Dashboard routing'} ${val ? 'ON' : 'OFF'}`); load(); }
    catch { toast.error('Update failed'); }
  };
  const setMode = async (inst: any, mode: 'PAPER' | 'LIVE') => {
    if (mode === 'LIVE' && !window.confirm('Switch to LIVE? Real naked-sell orders will be placed.')) return;
    try { await optionStrategyService.update(inst.id, { tradingMode: mode }); toast.success(`Mode → ${mode}`); load(); } catch { toast.error('Update failed'); }
  };
  const squareOff = async (inst: any) => {
    if (!window.confirm('Square off ALL open positions for this instance?')) return;
    try { const r = await optionStrategyService.squareOffAll(inst.id); toast.success(`Closed ${r.data?.data?.closed ?? 0}`); load(); } catch { toast.error('Square-off failed'); }
  };
  const squareOffSymbol = async (inst: any, symbol: string) => {
    if (!window.confirm(`Square off the open ${symbol} position for "${inst.name}"?`)) return;
    try { const r = await optionStrategyService.squareOffAll(inst.id, symbol); toast.success(`Closed ${r.data?.data?.closed ?? 0} (${symbol})`); load(); } catch { toast.error('Square-off failed'); }
  };
  const killAll = async () => {
    if (!instances.length || !window.confirm('PORTFOLIO KILL SWITCH — square off open positions across ALL instances?')) return;
    setBusy(true);
    try { let total = 0; for (const inst of instances) { const r = await optionStrategyService.squareOffAll(inst.id); total += r.data?.data?.closed ?? 0; } toast.success(`Closed ${total} position(s)`); load(); }
    catch { toast.error('Kill switch failed'); }
    setBusy(false);
  };
  // Instance tabs are now Monitor (0) + Performance (1) only — Rules / Backtest /
  // Settings moved up to the strategy card.
  const setTab = (instId: number, tab: number) => {
    setTabByInst(prev => ({ ...prev, [instId]: tab }));
    if (tab === 0) loadLog(instId);
  };


  return (
    <Box sx={{ p: embedded ? 0 : 2, width: '100%' }}>
      {/* In the Strategy hub the tab label says "Options AT"; keep the action
          buttons but drop the redundant page title when embedded. */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        {!embedded && <Typography variant="h5" sx={{ fontWeight: 800 }}>Option Strategies</Typography>}
        <Tooltip arrow title="Build an option trade from a plain-language outlook — now on the Options hub (Trade tab), with beginner guidance.">
          <Button size="small" variant="outlined" endIcon={<OpenInNewIcon fontSize="small" />} onClick={() => navigate('/options?tab=trade')}>Build a trade</Button>
        </Tooltip>
        <Tooltip arrow title="Open the beginner-friendly Options hub — one place for building, monitoring and learning.">
          <Button size="small" variant="text" endIcon={<OpenInNewIcon fontSize="small" />} onClick={() => navigate('/options')}>Options hub</Button>
        </Tooltip>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setShowCreate(s => !s)}>{showCreate ? 'Close' : '+ New instance'}</Button>
          <Button color="error" variant="contained" size="small" disabled={busy || !instances.length} onClick={killAll}>🛑 Square off ALL</Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <b>Configure &amp; deep-manage</b> your 24×7 option auto-trade engines on Delta (BTCUSD/ETHUSD) — create instances, set defaults, read the rules, backtest, watch the trade-by-trade grid + decision log, and go LIVE / square off. For a quick glance or to build a one-off trade, use the <b>Options hub</b>. New instances start in <b>PAPER</b> (simulated, no broker orders) — flip to LIVE only after validating.
      </Typography>

      {archMode === 'instances' && (<>
      {/* Available strategies — each card holds its DEFAULT settings + Rules + Backtest,
          and a Create button. New instances inherit the saved defaults. */}
      {registry.length > 0 && (() => {
        const ACCENTS = ENGINE_ACCENTS;
        const btSyms = Array.from(new Set(watchlists.flatMap((w: any) => (w.symbols || []).filter((s: any) => (s.exchange || '').toUpperCase() === 'DELTA').map((s: any) => s.symbol))));
        const cardBtSyms = btSyms.length ? (btSyms as string[]) : ['BTCUSD', 'ETHUSD'];
        return (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '0.9rem' }}>
            Available strategies
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({registry.length}) — set default settings, read the rules, backtest, then create an instance</Typography>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {registry.map((d, di) => {
              const accent = ACCENTS[di % ACCENTS.length];
              const hasSavedDefault = !!defaults[d.id];
              return (
                <Card key={d.id} variant="outlined" sx={{ p: 0, flex: '1 1 320px', minWidth: 280, maxWidth: 420, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderTop: `3px solid ${accent}` }}>
                  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography sx={{ fontWeight: 800, color: accent }}>{d.label}</Typography>
                      <Chip size="small" label={d.id} variant="outlined" sx={{ height: 18, fontSize: '0.58rem' }} />
                      {hasSavedDefault && <Chip size="small" label="custom defaults" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.56rem' }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, flex: 1 }}>{d.description}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button size="small" variant="contained" onClick={() => { setStrategyId(d.id); setName(d.label); setShowCreate(true); }}>+ Create instance</Button>
                      <Button size="small" variant="outlined" startIcon={<SettingsIcon fontSize="small" />} onClick={() => { setCardOpenId(d.id); setCardTab(prev => ({ ...prev, [d.id]: prev[d.id] ?? 0 })); }}>Settings · Rules · Backtest</Button>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>

          {/* Settings · Rules · Backtest — a MODAL so it never reflows the card grid and
              reads as a distinct surface (fixes the push-down / same-as-tile confusion). */}
          {(() => {
            const d = registry.find(x => x.id === cardOpenId);
            if (!d) return null;
            const di = registry.findIndex(x => x.id === d.id);
            const accent = ACCENTS[Math.max(0, di) % ACCENTS.length];
            const ct = cardTab[d.id] ?? 0;
            const spec = OPTION_OVERVIEW_SPECS[d.id];
            const cp = cardParams[d.id] || {};
            const setCp = (k: string, v: any) => setCardParams(prev => ({ ...prev, [d.id]: { ...(prev[d.id] || {}), [k]: v } }));
            const numFields = d.params.filter(p => p.type !== 'boolean');
            const boolFields = d.params.filter(p => p.type === 'boolean');
            return (
              <Dialog open onClose={() => setCardOpenId(null)} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderTop: `4px solid ${accent}`, borderRadius: 2 } }}>
                <DialogTitle sx={{ py: 1.2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', bgcolor: `${accent}14` }}>
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: accent }} />
                  <Typography sx={{ fontWeight: 800, color: accent }}>{d.label}</Typography>
                  <Chip size="small" label={d.id} variant="outlined" sx={{ height: 18, fontSize: '0.56rem' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>Default settings · Rules · Backtest</Typography>
                  <IconButton size="small" onClick={() => setCardOpenId(null)} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                  <Tabs value={ct} onChange={(_e, v) => setCardTab(prev => ({ ...prev, [d.id]: v }))} sx={{ minHeight: 40, px: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}`, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                    <Tab label="Default settings" sx={{ minHeight: 40, py: 0 }} />
                    <Tab label="Rules" sx={{ minHeight: 40, py: 0 }} />
                    <Tab label="Backtest" sx={{ minHeight: 40, py: 0 }} />
                  </Tabs>
                  <Box sx={{ p: 2 }}>
                    {ct === 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          These become the starting settings for every <b>new</b> {d.label} instance. Save to apply them to future instances (existing instances are unchanged).
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.8, alignItems: 'start' }}>
                          {numFields.map(p => (
                            <Box key={p.key} sx={{ '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' } }}>
                              <ParamInput field={p} value={cp[p.key]} onChange={v => setCp(p.key, v)} />
                            </Box>
                          ))}
                        </Box>
                        {boolFields.length > 0 && (
                          <>
                            <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">Toggles</Typography></Divider>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, alignItems: 'center' }}>
                              {boolFields.map(p => <ParamInput key={p.key} field={p} value={cp[p.key]} onChange={v => setCp(p.key, v)} />)}
                            </Box>
                          </>
                        )}
                        <Box sx={{ mt: 2.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Button variant="contained" onClick={() => saveCardDefault(d.id)} disabled={savingDefault}>Save as default</Button>
                          <Typography variant="caption" color="text.secondary">Then “+ Create instance” uses these.</Typography>
                        </Box>
                      </>
                    )}
                    {ct === 1 && (spec ? <StrategyOverview spec={spec} /> : <Typography color="text.secondary">No rules documented.</Typography>)}
                    {ct === 2 && <OptionInstanceBacktest engine={d.id} config={buildConfig(cp, d)} intervalMinutes={15} symbols={cardBtSyms} />}
                  </Box>
                </DialogContent>
              </Dialog>
            );
          })()}
        </Box>
        );
      })()}

      {/* Watchlist manager removed — watchlists are created/edited on the
          Watchlist page (instances below only SELECT one). */}

      {/* Create instance (collapsible) */}
      {showCreate && (
        <Card sx={{ p: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>New instance</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1 }}>
            <Tooltip title={descriptor?.description || 'Strategy'} arrow disableInteractive slotProps={{ popper: { sx: { zIndex: 1250 } } }}><FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Strategy</InputLabel>
              <Select label="Strategy" value={strategyId} onChange={e => { setStrategyId(e.target.value); const d = registry.find(x => x.id === e.target.value); const init: Record<string, any> = {}; d?.params.forEach(p => init[p.key] = p.default); setParamVals(init); }}>
                {registry.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
              </Select>
            </FormControl></Tooltip>
            <TextField size="small" label="Name" value={name} onChange={e => setName(e.target.value)} sx={{ width: 150 }} />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Watchlist</InputLabel>
              <Select label="Watchlist" value={watchlistId} onChange={e => setWatchlistId(Number(e.target.value))}>
                {watchlists.map(w => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Tooltip title="The candle timeframe the strategy evaluates on (signals computed on each close)." arrow disableInteractive slotProps={{ popper: { sx: { zIndex: 1250 } } }}><FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Timeframe</InputLabel>
              <Select label="Timeframe" value={intervalMinutes} onChange={e => setIntervalMinutes(Number(e.target.value))}>{TF_OPTS.map(m => <MenuItem key={m} value={m}>{tfLabel(m)}</MenuItem>)}</Select>
            </FormControl></Tooltip>
            <Tooltip title="PAPER = full decisioning + simulated fills/P&L, NO broker orders. LIVE = real Delta orders. Start in PAPER." arrow disableInteractive slotProps={{ popper: { sx: { zIndex: 1250 } } }}><FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Mode</InputLabel>
              <Select label="Mode" value={tradingMode} onChange={e => setTradingMode(e.target.value as any)}><MenuItem value="PAPER">PAPER</MenuItem><MenuItem value="LIVE">LIVE</MenuItem></Select>
            </FormControl></Tooltip>
            <Tooltip title="Per-scan Auto-Trade switch. When OFF, the instance evaluates + logs but never places orders (forces PAPER behaviour). For ThetaMiner_Ultra: OFF = alert-only scanner." arrow disableInteractive slotProps={{ popper: { sx: { zIndex: 1250 } } }}>
              <FormControlLabel control={<Switch checked={autoTrade} onChange={e => setAutoTrade(e.target.checked)} />} label="Auto-trade" />
            </Tooltip>
            <Tooltip title="Route signals to the Dashboard's Recent Alerts." arrow disableInteractive slotProps={{ popper: { sx: { zIndex: 1250 } } }}>
              <FormControlLabel control={<Switch checked={routeDashboard} onChange={e => setRouteDashboard(e.target.checked)} />} label="Dashboard" />
            </Tooltip>
            <Tooltip title="Send a Telegram notification on each signal." arrow disableInteractive slotProps={{ popper: { sx: { zIndex: 1250 } } }}>
              <FormControlLabel control={<Switch checked={routeTelegram} onChange={e => setRouteTelegram(e.target.checked)} />} label="Telegram" />
            </Tooltip>
          </Box>
          <Box sx={{ mt: 0.5, mb: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              Uses the saved <b>default settings</b> for {descriptor?.label || 'this strategy'}
              {defaults[strategyId] ? '' : ' (registry defaults — none customised yet)'}. Edit them in the strategy card’s <b>Default settings</b> tab.
            </Typography>
            <Button size="small" variant="text" onClick={() => { setCardOpenId(strategyId); setCardTab(prev => ({ ...prev, [strategyId]: 0 })); }}>Open settings</Button>
          </Box>
          <Box sx={{ mt: 0.5 }}><Button variant="contained" onClick={create} disabled={busy}>Create instance</Button></Box>
        </Card>
      )}

      {/* Instances */}
      {instances.length === 0 && <Typography color="text.secondary">{loading ? 'Loading…' : 'No instances yet — click "New instance".'}</Typography>}
      {instances.map(inst => {
        const tab = tabByInst[inst.id] ?? 0;
        const events = logByInst[inst.id] || [];
        // Multi-leg engines track cycles (not single-leg positions) — stat from cycles so
        // the header P&L/trades/ROI match the Monitor grid.
        const st = inst.multiLeg ? cycleStats(inst.cycles || []) : computeInstanceStats(inst.positions || [], events);
        const expanded = expandedByInst[inst.id] ?? false;
        const live = inst.config?.tradingMode === 'LIVE';
        const enabled = !!inst.schedules?.[0]?.enabled;
        const stopProp = (e: React.SyntheticEvent) => e.stopPropagation();
        const accent = engineAccent(inst.engine || 'sudarsan', registry);
        return (
          <Accordion key={inst.id} expanded={expanded}
            onChange={(_e, exp) => setExpandedByInst(prev => ({ ...prev, [inst.id]: exp }))}
            TransitionProps={{ unmountOnExit: true }}
            disableGutters
            sx={{ mb: 1.5, borderRadius: 1.5, border: theme => `1px solid ${theme.palette.divider}`, borderLeft: `4px solid ${accent}`, '&:before': { display: 'none' }, overflow: 'hidden', boxShadow: expanded ? 3 : 0 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}
              sx={{ bgcolor: expanded ? `${accent}22` : `${accent}12`, borderBottom: expanded ? `2px solid ${accent}` : 'none', '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, flexWrap: 'wrap', my: 0.6 } }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
              <Typography sx={{ fontWeight: 800, color: accent }}>{inst.name}</Typography>
              <Chip size="small" label={inst.engine} sx={{ height: 18, fontSize: '0.56rem', fontWeight: 700, bgcolor: `${accent}22`, color: accent }} />
              <Chip size="small" label={inst.config?.tradingMode} color={live ? 'error' : 'default'} sx={{ height: 20 }} />
              <Chip size="small" label={`${inst.config?.expiryMode} · ${tfLabel(inst.schedules?.[0]?.intervalMinutes || 0)}`} variant="outlined" sx={{ height: 20 }} />
              <Tooltip title={inst.schedules?.[0] ? `Last run: ${inst.schedules[0].lastRunAt ? new Date(inst.schedules[0].lastRunAt).toLocaleString('en-IN') : '—'} (${inst.schedules[0].lastRunStatus || '—'})` : 'No schedule'}>
                <Chip size="small" label={enabled ? 'Enabled' : 'Paused'} color={enabled ? 'success' : 'default'} sx={{ height: 20 }} />
              </Tooltip>
              {/* Key stats — visible without expanding (issue #1) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 1, flexWrap: 'wrap' }}>
                <HeaderStat label="Open" value={`${st.openCount}`} />
                <HeaderStat label="Trades" value={`${st.trades}`} />
                <HeaderStat label="Total P&L" value={inrShort(st.total)} color={st.total >= 0 ? 'success.main' : 'error.main'} />
                <HeaderStat label="Last trade" value={st.lastTradePnl == null ? '—' : inrShort(st.lastTradePnl)} color={st.lastTradePnl == null ? 'text.disabled' : st.lastTradePnl >= 0 ? 'success.main' : 'error.main'} />
              </Box>
              {/* Controls — stop propagation so they don't toggle the accordion */}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, alignItems: 'center' }} onClick={stopProp}>
                <Tooltip arrow title={enabled ? 'Strategy is running — turn OFF to pause scanning (open positions are NOT closed)' : 'Turn ON to resume scanning on each candle close'}>
                  <FormControlLabel sx={{ mr: 0.5 }} control={<Switch size="small" checked={enabled} onChange={e => toggleEnabled(inst, e.target.checked)} />} label="On" />
                </Tooltip>
                {/* Routing trio (staged rollout): AT = Auto-Trade, DB = Dashboard alerts, TG = Telegram. */}
                {(() => {
                  const sc = inst.schedules?.[0];
                  if (!sc) return null;
                  const mk = (key: 'autoTrade' | 'routeDashboard' | 'routeTelegram', label: string, val: boolean, tip: string) => (
                    <Tooltip arrow key={key} title={tip}>
                      <Chip size="small" clickable label={label} onClick={() => toggleScanFlag(inst, key, !val)}
                        color={val ? 'primary' : 'default'} variant={val ? 'filled' : 'outlined'}
                        sx={{ height: 20, fontSize: '0.6rem', fontWeight: 800, '& .MuiChip-label': { px: 0.6 } }} />
                    </Tooltip>
                  );
                  return (<>
                    {mk('autoTrade', 'AT', !!sc.autoTrade, sc.autoTrade ? 'Auto-Trade ON — signals place orders. Click for alert-only.' : 'Auto-Trade OFF — alert-only scanner. Click to let signals place orders.')}
                    {mk('routeDashboard', 'DB', sc.routeDashboard !== false, 'Route each signal to the Dashboard\'s Recent Alerts.')}
                    {mk('routeTelegram', 'TG', !!sc.routeTelegram, 'Send a Telegram notification per signal.')}
                  </>);
                })()}
                <Tooltip arrow title={live ? 'Currently LIVE (real orders). Click to switch to PAPER (simulated, no broker orders).' : 'Currently PAPER (simulated). Click to switch to LIVE — real naked-sell orders will be placed on Delta with real money.'}>
                  <Button size="small" variant="outlined" color={live ? 'error' : 'primary'} onClick={() => setMode(inst, live ? 'PAPER' : 'LIVE')}>{live ? '→ PAPER' : '→ LIVE'}</Button>
                </Tooltip>
                <Tooltip arrow title="Immediately close ALL open positions for this instance (LIVE places real reduce-only exits; PAPER simulates).">
                  <Button size="small" color="error" variant="outlined" onClick={() => squareOff(inst)}>Square off</Button>
                </Tooltip>
                <Tooltip arrow title="Delete this instance — squares off any open positions first, then removes it (config, schedule, history). Cannot be undone.">
                  <span><IconButton size="small" color="error" disabled={deletingId === inst.id} onClick={() => deleteInstance(inst)}>{deletingId === inst.id ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon fontSize="small" />}</IconButton></span>
                </Tooltip>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
            <Tabs value={tab} onChange={(_e, v) => setTab(inst.id, v)} sx={{ minHeight: 36, px: 1, borderBottom: theme => `1px solid ${theme.palette.divider}` }}>
              <Tab label="Monitor" sx={{ minHeight: 36, py: 0 }} />
              <Tab label="Performance" sx={{ minHeight: 36, py: 0 }} />
            </Tabs>

            <Box sx={{ p: 1.5 }}>
              {/* MONITOR */}
              {tab === 0 && (
                <>
                  {inst.multiLeg ? (<CycleGrid cycles={inst.cycles || []} />) : ((!inst.positions || inst.positions.length === 0) ? (
                    <Typography variant="body2" color="text.secondary">No symbol state yet (runs on the next candle close).</Typography>
                  ) : (() => { const psp = perSymbolPnl(events); return (
                    <Table size="small">
                      <TableHead><TableRow>{['Symbol', 'U', 'CE', 'PE', 'Status', 'Held', 'Qty', 'Entry', 'Run P&L', "Day P&L", 'ESL (dist)', 'Data', 'Last eval', ''].map((h, i) => <TableCell key={h || `c${i}`} align={['Run P&L', "Day P&L"].includes(h) ? 'right' : 'left'} sx={{ fontWeight: 700, fontSize: '0.68rem', px: 0.8 }}>{h}</TableCell>)}</TableRow></TableHead>
                      <TableBody>
                        {inst.positions.map((p: any) => {
                          const d = eslDist(p);
                          const pnl = psp[p.symbol];
                          return (
                            <TableRow key={p.id}>
                              <TableCell sx={{ fontSize: '0.72rem', px: 0.8 }}>{p.symbol}</TableCell>
                              <TableCell sx={{ px: 0.8 }}><BandBadge band={p.prevBandU} score={p.lastScoreU} /></TableCell>
                              <TableCell sx={{ px: 0.8 }}><Tooltip arrow title={p.targetCeSymbol || 'target CE (ATM+1)'}><span><BandBadge band={p.prevBandCE} score={p.lastScoreCE} /></span></Tooltip></TableCell>
                              <TableCell sx={{ px: 0.8 }}><Tooltip arrow title={p.targetPeSymbol || 'target PE (ATM−1)'}><span><BandBadge band={p.prevBandPE} score={p.lastScorePE} /></span></Tooltip></TableCell>
                              <TableCell sx={{ px: 0.8 }}><Chip size="small" label={p.status} color={p.status === 'FLAT' ? 'default' : 'warning'} sx={{ height: 18, fontSize: '0.6rem' }} /></TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', px: 0.8 }}>{p.heldSymbol || '—'}</TableCell>
                              <TableCell sx={{ fontSize: '0.72rem', px: 0.8 }}>{p.qty ?? '—'}</TableCell>
                              <TableCell sx={{ fontSize: '0.72rem', px: 0.8 }}>{p.entryPremium ?? '—'}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', px: 0.8, fontWeight: 700, color: !pnl ? 'text.disabled' : pnl.running >= 0 ? 'success.main' : 'error.main' }}>{pnl ? inrShort(pnl.running) : '—'}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', px: 0.8, fontWeight: 700, color: !pnl ? 'text.disabled' : pnl.day >= 0 ? 'success.main' : 'error.main' }}>{pnl ? inrShort(pnl.day) : '—'}</TableCell>
                              <TableCell sx={{ fontSize: '0.72rem', px: 0.8 }}>{p.eslLevel != null ? `${p.eslLevel}${d != null ? ` (${d.toFixed(1)}%)` : ''}` : '—'}</TableCell>
                              <TableCell sx={{ px: 0.8 }}><Chip size="small" label={p.lastDataStatus || '—'} color={p.lastDataStatus === 'OK' ? 'success' : p.lastDataStatus === 'FAILED' ? 'error' : 'default'} sx={{ height: 18, fontSize: '0.6rem' }} /></TableCell>
                              <TableCell sx={{ fontSize: '0.66rem', px: 0.8 }}>{p.lastEvalAt ? new Date(p.lastEvalAt).toLocaleTimeString('en-IN') : '—'}</TableCell>
                              <TableCell sx={{ px: 0.4 }}>{p.status !== 'FLAT' && p.heldSymbol && (
                                <Tooltip arrow title={`Square off ${p.symbol} now (${inst.config?.tradingMode === 'LIVE' ? 'real reduce-only exit' : 'simulated'})`}>
                                  <IconButton size="small" color="error" onClick={() => squareOffSymbol(inst, p.symbol)}><CancelIcon sx={{ fontSize: 16 }} /></IconButton>
                                </Tooltip>
                              )}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  ); })())}
                  {/* Decision log with Reason column — collapsible */}
                  {(() => { const logOpen = logOpenByInst[inst.id] ?? true; return (<>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mt: 1.5, mb: 0.8,
                    bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(167,139,250,0.15)' : 'rgba(107,33,168,0.10)',
                    borderLeft: theme => `3px solid ${theme.palette.mode === 'dark' ? '#A78BFA' : '#6B21A8'}`,
                    borderRadius: '4px', px: 0.8, py: 0.4 }}
                    onClick={() => setLogOpenByInst(prev => ({ ...prev, [inst.id]: !logOpen }))}>
                    <IconButton size="small" sx={{ p: 0.2, color: theme => theme.palette.mode === 'dark' ? '#A78BFA' : '#6B21A8', transform: logOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 150ms' }}><ExpandMoreIcon fontSize="small" /></IconButton>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: theme => theme.palette.mode === 'dark' ? '#A78BFA' : '#6B21A8', textTransform: 'uppercase', letterSpacing: 0.4 }}>Decision log</Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled', ml: 0.5 }}>({events.length})</Typography>
                  </Box>
                  <Collapse in={logOpen} unmountOnExit>
                  <Box sx={{ maxHeight: 340, overflow: 'auto', border: theme => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead><TableRow>{['Time', 'Symbol', 'U', 'CE', 'PE', 'Action', 'P&L', 'Reason'].map(h => <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.66rem', px: 0.8 }}>{h}</TableCell>)}</TableRow></TableHead>
                      <TableBody>
                        {events.length === 0 && <TableRow><TableCell colSpan={8} sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>No entries yet.</TableCell></TableRow>}
                        {events.map((e: any) => (
                          <TableRow key={e.id}>
                            <TableCell sx={{ fontSize: '0.64rem', px: 0.8, whiteSpace: 'nowrap' }}>{new Date(e.at).toLocaleTimeString('en-IN')}</TableCell>
                            <TableCell sx={{ fontSize: '0.66rem', px: 0.8 }}>{e.symbol}</TableCell>
                            <TableCell sx={{ px: 0.8 }}><BandBadge band={e.bandU} score={e.scoreU} /></TableCell>
                            <TableCell sx={{ px: 0.8 }}><BandBadge band={e.bandCE} score={e.scoreCE} /></TableCell>
                            <TableCell sx={{ px: 0.8 }}><BandBadge band={e.bandPE} score={e.scorePE} /></TableCell>
                            <TableCell sx={{ px: 0.8 }}><Chip size="small" label={e.action} sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: ACTION_COLOR[e.action] || '#90a4ae', color: '#fff' }} /></TableCell>
                            <TableCell sx={{ fontSize: '0.66rem', px: 0.8, color: e.pnl == null ? 'text.disabled' : (e.pnl >= 0 ? 'success.main' : 'error.main') }}>{e.pnl == null ? '—' : Math.round(e.pnl)}</TableCell>
                            <TableCell sx={{ fontSize: '0.66rem', px: 0.8, maxWidth: 420 }}>{e.reason || e.detail || ''}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                  </Collapse>
                  </>); })()}
                  {inst.positions?.some((p: any) => p.lastError) && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                      {inst.positions.filter((p: any) => p.lastError).map((p: any) => `${p.symbol}: ${p.lastError}`).join(' · ')}
                    </Typography>
                  )}
                </>
              )}

              {/* PERFORMANCE (Rules / Backtest / Settings now live on the strategy card) */}
              {tab === 1 && <OptionInstancePerformance positions={inst.positions || []} events={events} cycles={inst.cycles || []} multiLeg={!!inst.multiLeg} />}
            </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Band legend */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">Bands:</Typography>
        {Object.entries(BAND_META).map(([k, m]) => <Chip key={k} size="small" label={`${m.abbr} ${m.label}`} sx={{ height: 18, fontSize: '0.6rem', bgcolor: m.bg, color: m.fg }} />)}
      </Box>
      </>)}
    </Box>
  );
};

export default OptionStrategiesPage;
